import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import CreateProfilePage from "./pages/CreateProfilePage";
import HomePage from "./pages/HomePage";
import PrayerWallPage from "./pages/PrayerWallPage";
import BenevolentPage from "./pages/BenevolentPage";
import DirectoryPage from "./pages/DirectoryPage";
import ProfilePage from "./pages/ProfilePage";
import AdminPage from "./pages/AdminPage";
import BiblePage from "./pages/BiblePage";
import MinistryTeamsPage from "./pages/MinistryTeamsPage";
import GroupChatPage from "./pages/GroupChatPage";
import NotificationsPage from "./pages/NotificationsPage";
import MessagesPage from "./pages/MessagesPage";
import DirectMessagePage from "./pages/DirectMessagePage";
import HelpPage from "./pages/HelpPage";
import InspirationalPage from "./pages/InspirationalPage";

// Update Available Banner Component
const UpdateBanner = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    // Badge is kept in sync with the real unread count by AuthContext; do not force-clear here.
    
    // Listen for service worker update messages
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'SW_UPDATED') {
          console.log('App update available:', event.data.version);
          setUpdateAvailable(true);
        }
      });

      // Also check for waiting service worker on load
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.waiting) {
          // Auto-activate the waiting service worker to prevent browser prompt
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          setUpdateAvailable(true);
        }
        
        // Listen for new service worker installing
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // Auto-activate to prevent browser's native prompt
                newWorker.postMessage({ type: 'SKIP_WAITING' });
                setUpdateAvailable(true);
              }
            });
          }
        });
        
        // Force check for updates on app load
        registration.update();
      });

      // Check for updates every 2 minutes (more frequent)
      const checkInterval = setInterval(() => {
        navigator.serviceWorker.ready.then((registration) => {
          registration.update();
        });
      }, 2 * 60 * 1000);

      return () => clearInterval(checkInterval);
    }
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    
    try {
      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        console.log('All caches cleared');
      }
      
      // Tell the waiting service worker to skip waiting and activate
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
        // Unregister and re-register service worker
        await registration.unregister();
        console.log('Service worker unregistered');
      }
    } catch (error) {
      console.error('Error during refresh:', error);
    }
    
    // Force reload from server (bypass cache)
    window.location.href = window.location.href.split('?')[0] + '?refresh=' + Date.now();
  };

  if (!updateAvailable) return null;

  return (
    <div className="fixed top-4 right-4 z-[9999]">
      <button
        onClick={handleRefresh}
        disabled={refreshing}
        className="bg-green-600 text-white px-4 py-3 rounded-xl text-sm font-bold hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2 shadow-lg"
      >
        {refreshing ? (
          <>
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Updating...
          </>
        ) : (
          <>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Update App
          </>
        )}
      </button>
    </div>
  );
};

// Protected Route Component - redirects to create-profile if profile not complete
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-tan-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to create profile if profile is not complete
  if (user && !user.profileComplete) {
    return <Navigate to="/create-profile" replace />;
  }

  return children;
};

// Profile Creation Route - only accessible if logged in but profile not complete
const ProfileCreationRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-tan-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If profile is already complete, redirect to home
  if (user && user.profileComplete) {
    return <Navigate to="/home" replace />;
  }

  return children;
};

// Public Route (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-tan-500 border-t-transparent"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    // Check if profile is complete
    if (user && !user.profileComplete) {
      return <Navigate to="/create-profile" replace />;
    }
    return <Navigate to="/home" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Landing Page */}
      <Route
        path="/"
        element={
          <PublicRoute>
            <LandingPage />
          </PublicRoute>
        }
      />
      
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />

      {/* Profile Creation Route */}
      <Route
        path="/create-profile"
        element={
          <ProfileCreationRoute>
            <CreateProfilePage />
          </ProfileCreationRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prayer-wall"
        element={
          <ProtectedRoute>
            <PrayerWallPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/benevolent"
        element={
          <ProtectedRoute>
            <BenevolentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bible"
        element={
          <ProtectedRoute>
            <BiblePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/directory"
        element={
          <ProtectedRoute>
            <DirectoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ministry-teams"
        element={
          <ProtectedRoute>
            <MinistryTeamsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/group-chat/:groupId"
        element={
          <ProtectedRoute>
            <GroupChatPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dm/:userId"
        element={
          <ProtectedRoute>
            <DirectMessagePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <HelpPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/inspirational"
        element={
          <ProtectedRoute>
            <InspirationalPage />
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <UpdateBanner />
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
